/** Campaign setup wizard (Campaigns/Sessions v2 #1-#7).
 *
 * A dedicated stepped flow used to both CREATE/complete a draft AND edit an
 * already-active campaign (the pencil on the detail page routes here for
 * every status). Step 1 creates (or, on resume, patches) the campaign;
 * steps 2-7 reuse the existing detail-page cards (operators, custom fields,
 * beneficiaries, questions, slot rhythm, templates). Custom fields come BEFORE
 * beneficiaries so they exist as import-template columns. The final step
 * ACTIVATES a draft
 * (draft → active, which arms the automatic daily wave); for an already-
 * active campaign it's just "Save & close" — activation is a one-time step
 * and the invitation wave then runs on its own.
 *
 * Routes: /campaigns/new  and  /campaigns/:id/wizard.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useWaveSummary } from "@/modules/campaigns/useWaveSummary";
import { useNavigate, useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { CountrySelector } from "@/components/CountrySelector";
import { MuiDatePicker } from "@/components/MuiDatePicker";
import { Spinner } from "@/components/Spinner";
import { ApiPaths, api } from "@/lib/api";
import { useTimezones } from "@/lib/useTimezones";
import type { Campaign } from "@/lib/types";
import {
  createCampaign,
  patchCampaign,
  type PatchCampaignPayload,
} from "@/modules/campaigns/api";
import { CustomFieldsCard } from "@/modules/admin/CustomFieldsCard";
import { CampaignAssigneesCard } from "@/modules/campaigns/CampaignAssigneesCard";
import { CampaignLeads } from "@/modules/campaigns/CampaignLeads";
import { CampaignNotificationTemplatesCard } from "@/modules/campaigns/CampaignNotificationTemplatesCard";
import { CampaignQuestions } from "@/modules/campaigns/CampaignQuestions";
import { SlotSettingsStep } from "@/modules/campaigns/SlotSettingsStep";
import {
  DEFAULT_SLOT_SETTINGS,
  type SlotSettings,
} from "@/modules/campaigns/slots";

// "fields" precedes "beneficiaries" on purpose: campaign custom fields become
// columns in the beneficiary import template, so they must be defined BEFORE any
// list is imported — otherwise imported beneficiaries land with empty custom
// fields (you'd have to re-import to backfill).
const STEP_KEYS = [
  "details",
  "operators",
  "fields",
  "beneficiaries",
  "questions",
  "slots",
  "templates",
  "review",
] as const;


export default function CampaignWizardPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [campaignId, setCampaignId] = useState<string | null>(id ?? null);
  // The ORG's wave schedule: the campaign will inherit it, and at this
  // point in the wizard it may not exist yet.
  const waveSummary = useWaveSummary({ kind: "org" });
  const [step, setStep] = useState(0);
  const tzs = useTimezones();
  const [details, setDetails] = useState({
    name: "",
    description: "",
    country: "",
    start_date: "",
    end_date: "",
    campaign_kind: "standard" as "standard" | "ai_assisted",
    timezone: "",
  });
  const [slots, setSlots] = useState<SlotSettings>(DEFAULT_SLOT_SETTINGS);
  const [seeded, setSeeded] = useState(false);

  // Resume mode — prefill from the existing draft.
  const existing = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: async () =>
      (await api.get<Campaign>(ApiPaths.campaign(campaignId!))).data,
    enabled: !!campaignId,
  });

  useEffect(() => {
    if (seeded || !existing.data) return;
    const c = existing.data;
    setDetails({
      name: c.name ?? "",
      description: c.description ?? "",
      country: c.country ?? "",
      start_date: c.start_date ?? "",
      end_date: c.end_date ?? "",
      campaign_kind: (c.campaign_kind as "standard" | "ai_assisted") ?? "standard",
      timezone: c.timezone ?? "",
    });
    setSlots({
      slot_minutes: c.slot_minutes ?? DEFAULT_SLOT_SETTINGS.slot_minutes,
      break_minutes:
        c.break_minutes ?? DEFAULT_SLOT_SETTINGS.break_minutes,
      slack_minutes:
        c.slack_minutes ?? DEFAULT_SLOT_SETTINGS.slack_minutes,
      break_mode: (c.break_mode as SlotSettings["break_mode"]) ??
        DEFAULT_SLOT_SETTINGS.break_mode,
      calls_before_break:
        c.calls_before_break ?? DEFAULT_SLOT_SETTINGS.calls_before_break,
      minutes_before_break:
        c.minutes_before_break ??
        DEFAULT_SLOT_SETTINGS.minutes_before_break,
      long_break_minutes:
        c.long_break_minutes ?? DEFAULT_SLOT_SETTINGS.long_break_minutes,
    });
    setSeeded(true);
  }, [existing.data, seeded]);

  // Step 1 — create (or patch) the draft, then advance.
  const saveDetails = useMutation({
    mutationFn: async () => {
      const payload = {
        name: details.name.trim(),
        description: details.description.trim(),
        country: details.country || undefined,
        start_date: details.start_date || null,
        end_date: details.end_date || null,
        campaign_kind: details.campaign_kind,
        timezone: details.timezone,
      };
      if (campaignId) {
        return patchCampaign(campaignId, payload as PatchCampaignPayload);
      }
      return createCampaign(payload);
    },
    onSuccess: (c) => {
      setCampaignId(c.id);
      qc.setQueryData(["campaign", c.id], c);
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      setStep(1);
    },
  });

  // Slot-settings step — persist the slot rhythm before moving on to templates.
  const saveSlots = useMutation({
    mutationFn: async () =>
      patchCampaign(campaignId!, slots as PatchCampaignPayload),
    onSuccess: (c) => {
      qc.setQueryData(["campaign", c.id], c);
      setStep(6);
    },
  });

  const launch = useMutation({
    mutationFn: async () =>
      (await api.post(ApiPaths.campaignLaunch(campaignId!), {})).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${campaignId}`);
    },
  });

  if (campaignId && existing.isLoading && !seeded) {
    return <Spinner fullscreen label={t("common.loading")} />;
  }

  const canNextDetails = details.name.trim().length > 0;
  const goDraft = () => navigate(`/campaigns/${campaignId}`);
  // Already-active campaign → the wizard is an editor, not an activation
  // flow (the daily wave already runs on its own).
  const isLaunched = !!existing.data?.launched_at;

  return (
    <AppLayout
      title={isLaunched ? t("wizard.titleEdit") : t("wizard.title")}
      description={t("wizard.subtitle")}
      headerAction={
        <BackLink
          fallback={campaignId ? `/campaigns/${campaignId}` : "/campaigns"}
          className="btn-ghost text-sm"
        >
          ← {t("common.back")}
        </BackLink>
      }
    >
      <div className="space-y-4">
      {/* Step indicator */}
      <ol className="flex flex-wrap gap-1.5 text-xs">
        {STEP_KEYS.map((k, i) => {
          const reachable = i === 0 || !!campaignId;
          const active = i === step;
          return (
            <li key={k}>
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && setStep(i)}
                className={
                  "rounded-full px-3 py-1 " +
                  (active
                    ? "bg-nb-mint text-black"
                    : reachable
                      ? "bg-nb-surface text-nb-text-2 hover:bg-nb-surface-2"
                      : "bg-nb-surface/40 text-nb-text-3")
                }
              >
                {i + 1}. {t(`wizard.step.${k}`)}
              </button>
            </li>
          );
        })}
      </ol>

      <section className="card space-y-4">
        {step === 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("wizard.step.details")}
            </h2>
            <div>
              <label className="label" htmlFor="w-name">
                {t("wizard.details.name")}
              </label>
              <input
                id="w-name"
                className="input"
                value={details.name}
                onChange={(e) =>
                  setDetails({ ...details, name: e.target.value })
                }
              />
            </div>
            <div>
              <label className="label" htmlFor="w-desc">
                {t("wizard.details.description")}
              </label>
              <textarea
                id="w-desc"
                className="input min-h-[4rem]"
                value={details.description}
                onChange={(e) =>
                  setDetails({ ...details, description: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="label">{t("wizard.details.country")}</label>
                <CountrySelector
                  value={details.country}
                  onChange={(v) => setDetails({ ...details, country: v })}
                />
              </div>
              <div>
                <label className="label">{t("wizard.details.startDate")}</label>
                <MuiDatePicker
                  value={details.start_date}
                  onChange={(v) => setDetails({ ...details, start_date: v })}
                  ariaLabel={t("wizard.details.startDate")}
                />
              </div>
              <div>
                <label className="label">{t("wizard.details.endDate")}</label>
                <MuiDatePicker
                  value={details.end_date}
                  minDate={details.start_date || undefined}
                  onChange={(v) => setDetails({ ...details, end_date: v })}
                  ariaLabel={t("wizard.details.endDate")}
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="label">{t("wizard.details.timezone")}</label>
              <select
                className="input max-w-xs"
                value={details.timezone}
                onChange={(e) => setDetails({ ...details, timezone: e.target.value })}
              >
                <option value="">{t("wizard.details.timezoneInherit")}</option>
                {(tzs.data ?? []).map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-nb-text-2">
                {t("wizard.details.timezoneHelp")}
              </p>
            </div>
            <div className="mt-2 rounded-lg border border-nb-line bg-nb-surface-2 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={details.campaign_kind === "ai_assisted"}
                  disabled={!!existing.data?.launched_at}
                  onChange={(e) =>
                    setDetails({
                      ...details,
                      campaign_kind: e.target.checked ? "ai_assisted" : "standard",
                    })
                  }
                />
                <span>
                  <span className="block text-sm font-medium text-nb-text">
                    {t("wizard.details.aiAssistedLabel")}
                  </span>
                  <span className="block text-xs text-nb-text-2">
                    {t("wizard.details.aiAssistedHelp")}
                  </span>
                  {existing.data?.launched_at && (
                    <span className="mt-1 block text-xs text-cvp-warn">
                      {t("wizard.details.aiAssistedLocked")}
                    </span>
                  )}
                </span>
              </label>
            </div>
            {saveDetails.isError && (
              <p className="text-xs text-cvp-danger">
                {(saveDetails.error as Error)?.message ?? t("wizard.saveError")}
              </p>
            )}
          </div>
        )}

        {step === 1 && campaignId && (
          <CampaignAssigneesCard campaignId={campaignId} />
        )}
        {step === 2 && campaignId && (
          <div className="space-y-3">
            <p className="text-xs text-nb-text-2">
              {t("wizard.fieldsHelp")}
            </p>
            <CustomFieldsCard campaign={campaignId} />
          </div>
        )}
        {step === 3 && campaignId && (
          <CampaignLeads campaignId={campaignId} campaignName={details.name} />
        )}
        {step === 4 && campaignId && (
          <CampaignQuestions campaignId={campaignId} />
        )}
        {step === 5 && campaignId && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("wizard.step.slots")}
            </h2>
            <SlotSettingsStep
              campaignId={campaignId}
              value={slots}
              onChange={(patch) => setSlots((s) => ({ ...s, ...patch }))}
            />
          </div>
        )}
        {step === 6 && campaignId && (
          <CampaignNotificationTemplatesCard campaignId={campaignId} />
        )}
        {step === 7 && campaignId && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("wizard.step.review")}
            </h2>
            <p className="text-sm text-nb-text-2">{t("wizard.reviewHelp")}</p>
            <div className="rounded-md border border-nb-border bg-nb-surface/30 p-3 text-sm">
              <div className="font-medium text-nb-text">{details.name}</div>
              {details.description && (
                <p className="mt-1 whitespace-pre-wrap text-nb-text-2">
                  {details.description}
                </p>
              )}
              <p className="mt-2 text-xs text-nb-text-3">
                {t("wizard.reviewSlots", {
                  slot: slots.slot_minutes,
                  brk: slots.break_minutes,
                })}
              </p>
            </div>
            {!isLaunched && (
              <p className="rounded-md border border-nb-mint/25 bg-nb-mint/5 p-2.5 text-xs text-nb-text-2">
                {/* The org's schedule, not a hardcoded "10:00 Mon-Fri" —
                    the campaign does not exist yet, so it will inherit
                    this. */}
                {waveSummary.schedule
                  ? t("wizard.activateHelp", {
                      schedule: waveSummary.schedule,
                      zone: waveSummary.zone,
                    })
                  : t("wizard.activateHelpGeneric")}
              </p>
            )}
          </div>
        )}

        {/* Footer nav */}
        <div className="flex items-center justify-between border-t border-nb-border/50 pt-3">
          <button
            type="button"
            className="btn-ghost text-sm"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            {t("wizard.back")}
          </button>

          <div className="flex items-center gap-2">
            {campaignId && step > 0 && (
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={goDraft}
              >
                {t("wizard.saveDraft")}
              </button>
            )}

            {step === 0 && (
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={!canNextDetails || saveDetails.isPending}
                onClick={() => saveDetails.mutate()}
              >
                {saveDetails.isPending ? t("common.saving") : t("wizard.next")}
              </button>
            )}
            {step === 5 && (
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={saveSlots.isPending}
                onClick={() => saveSlots.mutate()}
              >
                {saveSlots.isPending ? t("common.saving") : t("wizard.next")}
              </button>
            )}
            {step > 0 && step !== 5 && step < 7 && (
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => setStep((s) => s + 1)}
              >
                {t("wizard.next")}
              </button>
            )}
            {step === 7 && isLaunched && (
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => navigate(`/campaigns/${campaignId}`)}
              >
                {t("wizard.saveClose")}
              </button>
            )}
            {step === 7 && !isLaunched && (
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={launch.isPending}
                onClick={() => launch.mutate()}
              >
                {launch.isPending
                  ? t("wizard.activating")
                  : t("wizard.activate")}
              </button>
            )}
          </div>
        </div>
        {launch.isError && (
          <p className="text-xs text-cvp-danger">
            {(launch.error as Error)?.message ?? t("wizard.saveError")}
          </p>
        )}
      </section>
      </div>
    </AppLayout>
  );
}
